import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Mail } from "lucide-react";
import { usePageMeta } from "@/hooks/use-page-meta";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Reveal = ({ children, delay = 0, className = "", testId }) => (
  <motion.div
    data-testid={testId}
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

const APPS = [
  {
    name: "Mu'min Kids",
    tag: "Islamic Learning for Kids",
    status: "Internal Testing",
    num: "01",
    description:
      "A safe, ad-free learning adventure for Muslim children ages 3-10 with Arabic letters and words, Quran and Hifz practice, duas, good manners, Wudu, Salah, Prophet stories, games, rewards, parent controls, and optional Plus/Family subscriptions. Core learning stays free.",
    image:
      "https://images.unsplash.com/photo-1622737133809-d95047b9e673?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMDNkJTIwc2hhcGUlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc4MjIxODU3N3ww&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-8",
    cta: {
      label: "Join Android Internal Testing",
      url: "https://play.google.com/apps/internaltest/4701055276445787097",
    },
  },
  {
    name: "Project Aurora",
    tag: "Lifestyle",
    status: "Coming Soon",
    num: "02",
    description:
      "A placeholder for a lifestyle app currently in development.",
    image:
      "https://images.pexels.com/photos/29708294/pexels-photo-29708294.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "md:col-span-4",
  },
  {
    name: "Project Tidepool",
    tag: "Utilities",
    status: "Coming Soon",
    num: "03",
    description:
      "A placeholder for a utility app exploring calm, focused design.",
    image:
      "https://images.unsplash.com/photo-1599422314077-f4dfdaa4cd09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGZsdWlkJTIwZGlnaXRhbCUyMGFydCUyMHRleHR1cmV8ZW58MHx8fHwxNzg0Mzk1NDk1fDA&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-12",
  },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const sunY = useTransform(scrollYProgress, [0, 1], [0, 260]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[92vh] flex-col justify-center overflow-hidden bg-[#0A0A0A] text-white"
    >
      <motion.div
        style={{ y: sunY }}
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[14%] z-0 -translate-x-1/2"
      >
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.8, delay: 0.15, ease: EASE }}
          className="relative h-[300px] w-[300px] md:h-[440px] md:w-[440px]"
        >
          <div className="absolute inset-0 rounded-full bg-[#F8F9FA]" />
          <div className="absolute left-[-8%] right-[-8%] top-[54%] h-[6%] bg-[#0A0A0A]" />
          <div className="absolute bottom-[-14%] left-1/2 h-[3%] w-[46%] -translate-x-1/2 rounded-full bg-[#F8F9FA]" />
        </motion.div>
      </motion.div>

      <motion.div
        style={{ y: contentY }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 py-28 md:px-8"
      >
        <motion.span
          data-testid="hero-eyebrow"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.95, duration: 0.8 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-white/60"
        >
          Independent App Publisher
        </motion.span>

        <h1
          data-testid="hero-heading"
          className="mt-8 font-heading text-[15vw] font-bold uppercase leading-[0.88] tracking-tight md:text-[9.5rem]"
        >
          <MaskedLine delay={0.35}>Bright</MaskedLine>
          <MaskedLine delay={0.5} className="text-outline">
            Frontier
          </MaskedLine>
          <MaskedLine delay={0.65}>Labs</MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subheading"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8, ease: EASE }}
          className="mt-10 max-w-xl text-base leading-relaxed text-white/60 md:text-lg"
        >
          We design, build, and publish mobile applications &mdash; crafted
          quietly, shipped boldly, and built for what&rsquo;s next.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="mailto:support@brightfrontierlabs.com"
            data-testid="hero-contact-button"
            className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-[#0A0A0A] transition-transform duration-200 hover:-translate-y-1 hover:shadow-[0_10px_40px_rgba(255,255,255,0.15)]"
          >
            Get in touch
            <ArrowUpRight className="h-4 w-4" />
          </a>
          <a
            href="#manifesto"
            data-testid="hero-manifesto-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/10"
          >
            Our manifesto
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};

const MARQUEE_ITEMS = [
  "Mobile Experiences",
  "Independent Publisher",
  "Craft Over Noise",
  "Privacy First",
  "Built For What's Next",
];

const Marquee = () => (
  <section
    aria-hidden
    data-testid="marquee-section"
    className="overflow-hidden border-b border-black/10 bg-white py-6"
  >
    <div className="marquee-track flex w-max items-center">
      {[0, 1].map((copy) => (
        <div key={copy} className="flex items-center">
          {MARQUEE_ITEMS.map((item) => (
            <span
              key={`${copy}-${item}`}
              className="mx-8 flex items-center gap-16 font-heading text-lg font-bold uppercase tracking-widest text-[#0A0A0A]"
            >
              {item}
              <span className="text-black/30">/</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </section>
);

const CHAPTERS = [
  {
    num: "01",
    title: "Craft over noise",
    text: "The app stores are full of noise. We publish a small number of titles and sweat every detail — typography, motion, performance — until each one feels inevitable.",
  },
  {
    num: "02",
    title: "Privacy is a feature",
    text: "We collect the minimum, explain it plainly, and never sell data. Respect for the person holding the phone is non-negotiable.",
  },
  {
    num: "03",
    title: "Built for what's next",
    text: "The frontier keeps moving. We design for where mobile is going — new platforms, new interfaces, new habits — not where it's been.",
  },
];

const Manifesto = () => (
  <section
    id="manifesto"
    data-testid="manifesto-section"
    className="border-b border-black/10 bg-[#F8F9FA]"
  >
    <div className="mx-auto max-w-7xl px-6 py-24 md:px-8 md:py-32">
      <Reveal>
        <span className="text-xs font-semibold uppercase tracking-widest text-[#4B5563]">
          Manifesto
        </span>
        <h2 className="mt-4 max-w-2xl font-heading text-4xl font-bold tracking-tight text-[#0A0A0A] sm:text-5xl">
          Three things we refuse to compromise.
        </h2>
      </Reveal>
      <div className="mt-16">
        {CHAPTERS.map((chapter, i) => (
          <Reveal
            key={chapter.num}
            delay={i * 0.08}
            testId={`manifesto-chapter-${chapter.num}`}
            className="group grid gap-4 border-t border-black/10 py-10 md:grid-cols-12 md:items-baseline"
          >
            <span className="font-heading text-sm font-bold tracking-widest text-[#4B5563] md:col-span-2">
              ({chapter.num})
            </span>
            <h3 className="font-heading text-2xl font-bold tracking-tight text-[#0A0A0A] transition-transform duration-300 group-hover:translate-x-2 md:col-span-4 md:text-3xl">
              {chapter.title}
            </h3>
            <p className="text-sm leading-relaxed text-[#4B5563] md:col-span-6 md:text-base">
              {chapter.text}
            </p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const AppCard = ({ app, index }) => (
  <Reveal
    delay={index * 0.08}
    testId={`app-card-${index}`}
    className={`group relative col-span-1 ${app.span}`}
  >
    <div className="relative overflow-hidden bg-[#0A0A0A]">
      <img
        src={app.image}
        alt={`${app.name} artwork`}
        data-testid={`app-card-image-${index}`}
        className="max-h-64 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        loading="lazy"
      />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.14),transparent_60%)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
    </div>
    <div className="border border-t-0 border-black/10 bg-white p-8">
      <span className="text-xs font-semibold uppercase tracking-widest text-[#4B5563]">
        App {app.num} &middot; {app.tag} &middot; {app.status}
      </span>
      <h3 className="mt-2 font-heading text-2xl font-bold tracking-tight text-[#0A0A0A]">
        {app.name}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-[#4B5563]">
        {app.description}
      </p>
      {app.cta && (
        <a
          href={app.cta.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`app-card-cta-${index}`}
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#0A0A0A] px-6 py-3 text-xs font-semibold text-white transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg"
        >
          {app.cta.label}
          <ArrowUpRight className="h-3.5 w-3.5" />
        </a>
      )}
    </div>
  </Reveal>
);

const AppsSection = () => (
  <section
    data-testid="apps-section"
    className="border-b border-black/10 bg-white"
  >
    <div className="mx-auto max-w-7xl px-6 py-24 md:px-8 md:py-32">
      <Reveal>
        <span className="text-xs font-semibold uppercase tracking-widest text-[#4B5563]">
          Portfolio
        </span>
        <h2
          data-testid="apps-heading"
          className="mt-4 max-w-2xl font-heading text-4xl font-bold tracking-tight text-[#0A0A0A] sm:text-5xl"
        >
          Our apps
        </h2>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-[#4B5563] md:text-base">
          Our first title, Mu&rsquo;min Kids, is now in Android internal
          testing &mdash; with more releases in development.
        </p>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12">
        {APPS.map((app, i) => (
          <AppCard key={app.name} app={app} index={i} />
        ))}
      </div>
    </div>
  </section>
);

const ContactSection = () => (
  <section
    id="contact"
    data-testid="contact-section"
    className="bg-[#0A0A0A] text-white"
  >
    <div className="mx-auto max-w-7xl px-6 py-24 md:px-8 md:py-32">
      <Reveal className="max-w-4xl">
        <a
          href="mailto:support@brightfrontierlabs.com"
          data-testid="contact-badge-email-link"
          className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-white/60 transition-colors duration-200 hover:bg-white/10 hover:text-white"
        >
          <Mail className="h-3.5 w-3.5" />
          Contact
        </a>
        <h2
          data-testid="contact-heading"
          className="mt-8 font-heading text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl"
        >
          We&rsquo;d love to hear from you.
        </h2>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-white/60 md:text-lg">
          Questions about our apps, support requests, press, or partnership
          enquiries &mdash; reach us anytime at our support inbox. We aim to
          respond within two business days.
        </p>
        <a
          href="mailto:support@brightfrontierlabs.com"
          data-testid="contact-mailto-link"
          className="group mt-12 inline-flex flex-wrap items-center font-heading text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl"
        >
          <span className="relative break-all">
            support@brightfrontierlabs.com
            <span className="absolute -bottom-2 left-0 h-[2px] w-full origin-left scale-x-0 bg-white transition-transform duration-500 ease-out group-hover:scale-x-100" />
          </span>
          <ArrowUpRight className="ml-2 h-6 w-6 shrink-0 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
        </a>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  usePageMeta(
    "Bright Frontier Labs — Mobile App Publisher",
    "Bright Frontier Labs is an independent mobile app publisher. Mu'min Kids — a safe, ad-free Islamic learning app for children ages 3-10 — is now in Android internal testing."
  );

  return (
    <>
      <Hero />
      <Marquee />
      <Manifesto />
      <AppsSection />
      <ContactSection />
    </>
  );
}
